// Shared Flatpickr config for the x-date-picker / x-datetime-picker
// directives (alpine-core-registrations.ts). Pure so it can be unit-tested
// without a DOM or the CDN-loaded flatpickr global.
//
// Locale: the page language (<html lang>, set from the resolved request
// language) picks a loaded flatpickr l10n (layout.templ loads l10n/de.js);
// an unknown or unloaded language falls back to flatpickr's English default.
// The visible (alt) format follows the locale's date order — German uses
// dd.mm.yyyy like internal/timefmt; the submitted value stays ISO.
//
// Bounds: optional data-min-date / data-max-date on the input (ISO
// "YYYY-MM-DD", optionally with "THH:MM"), parsed as LOCAL dates so a bound
// never shifts by a day in negative-offset zones.

interface FlatpickrGlobal {
  l10ns?: Record<string, unknown>;
}

interface Options {
  enableTime: boolean;
  lang: string;
  fp: FlatpickrGlobal;
  appendTo: HTMLElement | null;
}

interface AltFormats {
  date: string;
  dateTime: string;
}

const ALT_FORMATS: Record<string, AltFormats> = {
  de: { date: "d.m.Y", dateTime: "d.m.Y, H:i" },
  en: { date: "F j, Y", dateTime: "F j, Y, H:i" },
};

export function parseBoundDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?$/.exec(value.trim());
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] ?? 0), Number(m[5] ?? 0));
}

export function flatpickrConfig(el: HTMLElement, opts: Options): Record<string, unknown> {
  const lang = (opts.lang || "en").split("-")[0].toLowerCase();
  const locale = lang !== "en" ? opts.fp.l10ns?.[lang] : undefined;
  const formats: AltFormats = (locale ? ALT_FORMATS[lang] : undefined) ?? ALT_FORMATS.en;

  const config: Record<string, unknown> = {
    dateFormat: opts.enableTime ? "Y-m-d H:i" : "Y-m-d",
    altInput: true,
    altFormat: opts.enableTime ? formats.dateTime : formats.date,
    allowInput: true,
    static: false,
    appendTo: opts.appendTo || undefined,
    theme: "dark",
  };
  if (opts.enableTime) {
    config.enableTime = true;
    config.time_24hr = true;
  }
  if (locale) config.locale = locale;

  const minDate = parseBoundDate(el.dataset.minDate);
  const maxDate = parseBoundDate(el.dataset.maxDate);
  if (minDate) config.minDate = minDate;
  if (maxDate) config.maxDate = maxDate;
  return config;
}

/** Keeps a Flatpickr instance in step with its bound model value: sets the
 *  date when there is one, clears the picker (without firing onChange) when
 *  the model became empty — e.g. a reset or a removed blackout row. */
export function syncPickerToModel(
  picker: { setDate: (v: string, emit: boolean) => void; clear: (emit?: boolean) => void },
  value: unknown,
): void {
  if (typeof value === "string" && value !== "") picker.setDate(value, false);
  else picker.clear(false);
}
